import React, { useEffect, useMemo, useState } from 'react';
import Layout from '../../../components/Layout';
import ApiInspector from '../../../components/ApiInspector';
import PrintModal from '../../../components/PrintModal';
import { apiFetch } from '../../../lib/api';
import { useFiltroSocieta } from '../../../hooks/useFiltroSocieta';
import { API_DOCS } from './DatiCompletiSocietaPage.api';

// ── Tipi ──────────────────────────────────────────────────────────────────
interface BilancioLite {
  id: number;
  anno: number;
  tipo: 'UFFICIALE' | 'VERIFICA';
  commento: string | null;
  nomeFile: string;
}

interface ReportTemplateLite {
  id: number;
  nome: string;
  moduloGestionale: string;
  attivo: boolean;
}

type ChiaveSezione =
  | 'datiSocietari'
  | 'amministratori'
  | 'compagine'
  | 'sedi'
  | 'banche'
  | 'finanziamenti'
  | 'bilanci';

const SEZIONI: { key: ChiaveSezione; label: string; descrizione: string }[] = [
  { key: 'datiSocietari',  label: 'Dati societari',       descrizione: 'Anagrafica, sede legale, P.IVA, CF, capitale sociale' },
  { key: 'amministratori', label: 'Amministratori',       descrizione: 'Elenco amministratori con carica e documento di identità' },
  { key: 'compagine',      label: 'Compagine societaria', descrizione: 'Soci con quote percentuali' },
  { key: 'sedi',           label: 'Sedi',                 descrizione: 'Sedi operative e commerciali' },
  { key: 'banche',         label: 'Banche società',       descrizione: 'Banche con ultima condizione economica' },
  { key: 'finanziamenti',  label: 'Finanziamenti società',descrizione: 'Piano finanziamenti con stato rate' },
  { key: 'bilanci',        label: 'Bilanci società',      descrizione: 'Bilanci ufficiali e di verifica (selezione granulare)' },
];

type Flags = Record<ChiaveSezione, boolean>;
const INITIAL_FLAGS: Flags = {
  datiSocietari: true,
  amministratori: true,
  compagine: true,
  sedi: true,
  banche: true,
  finanziamenti: true,
  bilanci: true,
};

export default function DatiCompletiSocietaPage() {
  const {
    societaId,
    setSocietaId,
    listaSocieta,
    isDisabled,
    pronta,
  } = useFiltroSocieta();

  const [flags, setFlags] = useState<Flags>(INITIAL_FLAGS);

  // Bilanci disponibili per la società selezionata
  const [bilanciDisponibili, setBilanciDisponibili] = useState<BilancioLite[]>([]);
  const [bilanciSelezionati, setBilanciSelezionati] = useState<Set<number>>(new Set());
  const [tuttiBilanci, setTuttiBilanci] = useState(true);

  // Template DATI_SOCIETARI disponibili per la società selezionata
  const [templates, setTemplates] = useState<ReportTemplateLite[]>([]);
  const [templateId, setTemplateId] = useState<string>('');

  // PrintModal
  const [printModalOpen, setPrintModalOpen] = useState(false);

  const societaSelezionata = useMemo(
    () => listaSocieta.find(s => String(s.id) === String(societaId)) || null,
    [societaId, listaSocieta]
  );

  // Reset stato quando cambia società
  useEffect(() => {
    setBilanciDisponibili([]);
    setBilanciSelezionati(new Set());
    setTuttiBilanci(true);
    setTemplates([]);
    setTemplateId('');
  }, [societaId]);

  // Carica bilanci e template appena viene scelta una società
  useEffect(() => {
    if (!pronta || !societaId) return;
    let annullato = false;

    (async () => {
      try {
        // @api GET /api/bilanci?societaId=N -> [{...}]
        const bilanci = await apiFetch<BilancioLite[]>(`/api/bilanci?societaId=${societaId}`);
        if (annullato) return;
        setBilanciDisponibili(Array.isArray(bilanci) ? bilanci : []);
      } catch (e) {
        if (!annullato) setBilanciDisponibili([]);
        console.error('Errore caricamento bilanci', e);
      }

      try {
        // @api GET /api/report-templates?societaId=N&moduloGestionale=DATI_SOCIETARI&attivo=true -> { success, data: [...] }
        const resp = await apiFetch<{ success: boolean; data: ReportTemplateLite[] }>(
          `/api/report-templates?societaId=${societaId}&moduloGestionale=DATI_SOCIETARI&attivo=true`
        );
        if (annullato) return;
        const list = resp?.data || [];
        setTemplates(list);
        if (list.length === 1) setTemplateId(String(list[0].id));
      } catch (e) {
        if (!annullato) setTemplates([]);
        console.error('Errore caricamento template', e);
      }
    })();

    return () => { annullato = true; };
  }, [pronta, societaId]);

  const toggleFlag = (k: ChiaveSezione) => {
    setFlags(prev => ({ ...prev, [k]: !prev[k] }));
  };

  const toggleBilancio = (id: number) => {
    setBilanciSelezionati(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // ExtraOpts per PrintModal (flag sezioni + bilanciIds)
  const extraOpts = useMemo(() => {
    const out: Record<string, boolean | number[]> = {
      includiDatiSocietari:  flags.datiSocietari,
      includiAmministratori: flags.amministratori,
      includiCompagine:      flags.compagine,
      includiSedi:           flags.sedi,
      includiBanche:         flags.banche,
      includiFinanziamenti:  flags.finanziamenti,
      includiBilanci:        flags.bilanci,
    };
    if (flags.bilanci && !tuttiBilanci) {
      out.bilanciIds = Array.from(bilanciSelezionati);
    }
    return out;
  }, [flags, tuttiBilanci, bilanciSelezionati]);

  const almenoUnaSezione = Object.values(flags).some(v => v);
  const haTemplate = !!templateId;
  const puoStampare = !!societaId && almenoUnaSezione && haTemplate;

  return (
    <Layout>
      <div className="bop-rep-dati-soc">
        <div className="bop-page-header">
          <h1 className="bop-page-title">Report — Dati completi società</h1>
        </div>

        {/* ── Step 1: selezione società ────────────────────────────────── */}
        <div className="bop-card">
          <div className="bop-card__header">
            <h2 className="bop-card__title">1 · Società</h2>
          </div>
          <div className="bop-card__body">
            <div className="bop-form-row">
              <div className="bop-form-group">
                <label className="bop-label" htmlFor="rep-soc-select">Società oggetto del report</label>
                <select
                  id="rep-soc-select"
                  className="bop-select"
                  value={societaId}
                  onChange={e => setSocietaId(e.target.value)}
                  disabled={isDisabled}
                >
                  <option value="">— Seleziona società —</option>
                  {listaSocieta.map(s => (
                    <option key={s.id} value={String(s.id)}>{s.nome}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* ── Step 2: sezioni da stampare ──────────────────────────────── */}
        <div className="bop-card">
          <div className="bop-card__header">
            <h2 className="bop-card__title">2 · Sezioni da stampare</h2>
          </div>
          <div className="bop-card__body bop-rep-dati-soc__card-body--flush">
            <table className="bop-table">
              <thead>
                <tr>
                  <th className="bop-rep-dati-soc__th-flag">Stampa</th>
                  <th>Sezione</th>
                  <th>Descrizione</th>
                </tr>
              </thead>
              <tbody>
                {SEZIONI.map(s => (
                  <tr key={s.key}>
                    <td className="bop-rep-dati-soc__td-flag">
                      <input
                        type="checkbox"
                        className="bop-rep-dati-soc__checkbox"
                        aria-label={`Includi ${s.label}`}
                        checked={flags[s.key]}
                        onChange={() => toggleFlag(s.key)}
                      />
                    </td>
                    <td className="bop-rep-dati-soc__td-label">{s.label}</td>
                    <td className="bop-text-muted">{s.descrizione}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* ── Step 3: bilanci ──────────────────────────────────────────── */}
        {flags.bilanci && (
          <div className="bop-card">
            <div className="bop-card__header">
              <h2 className="bop-card__title">3 · Bilanci da includere</h2>
            </div>
            <div className="bop-card__body">
              {!societaId ? (
                <div className="bop-empty-state">
                  <p className="bop-empty-state__text">Seleziona prima una società.</p>
                </div>
              ) : bilanciDisponibili.length === 0 ? (
                <div className="bop-empty-state">
                  <p className="bop-empty-state__text">Nessun bilancio caricato per questa società.</p>
                </div>
              ) : (
                <>
                  <label className="bop-rep-dati-soc__toggle">
                    <input
                      type="checkbox"
                      className="bop-rep-dati-soc__checkbox"
                      checked={tuttiBilanci}
                      onChange={e => setTuttiBilanci(e.target.checked)}
                    />
                    <span>Includi tutti i bilanci disponibili</span>
                  </label>

                  {!tuttiBilanci && (
                    <div className="bop-rep-dati-soc__subtable">
                      <table className="bop-table">
                        <thead>
                          <tr>
                            <th className="bop-rep-dati-soc__th-flag">Includi</th>
                            <th>Anno</th>
                            <th>Tipo</th>
                            <th>Commento</th>
                            <th>File</th>
                          </tr>
                        </thead>
                        <tbody>
                          {bilanciDisponibili.map(b => (
                            <tr key={b.id}>
                              <td className="bop-rep-dati-soc__td-flag">
                                <input
                                  type="checkbox"
                                  className="bop-rep-dati-soc__checkbox"
                                  aria-label={`Includi bilancio ${b.anno}`}
                                  checked={bilanciSelezionati.has(b.id)}
                                  onChange={() => toggleBilancio(b.id)}
                                />
                              </td>
                              <td>{b.anno}</td>
                              <td>
                                <span className={`bop-badge ${b.tipo === 'UFFICIALE' ? 'bop-badge-success' : 'bop-badge-info'}`}>
                                  {b.tipo}
                                </span>
                              </td>
                              <td>{b.commento || '—'}</td>
                              <td className="bop-text-muted">{b.nomeFile}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        )}

        {/* ── Step 4: template ─────────────────────────────────────────── */}
        <div className="bop-card">
          <div className="bop-card__header">
            <h2 className="bop-card__title">4 · Template di stampa</h2>
          </div>
          <div className="bop-card__body">
            {!societaId ? (
              <div className="bop-empty-state">
                <p className="bop-empty-state__text">Seleziona prima una società.</p>
              </div>
            ) : templates.length === 0 ? (
              <div className="bop-empty-state">
                <p className="bop-empty-state__text">
                  Nessun template di modulo "DATI_SOCIETARI" attivo per questa società.
                </p>
                <p className="bop-empty-state__text bop-rep-dati-soc__hint">
                  Caricalo in Impostazioni → Stampe e Report → Report.
                </p>
              </div>
            ) : (
              <div className="bop-form-row">
                <div className="bop-form-group">
                  <label className="bop-label" htmlFor="rep-soc-template">Template</label>
                  <select
                    id="rep-soc-template"
                    className="bop-select"
                    value={templateId}
                    onChange={e => setTemplateId(e.target.value)}
                  >
                    <option value="">— Seleziona template —</option>
                    {templates.map(t => (
                      <option key={t.id} value={String(t.id)}>{t.nome}</option>
                    ))}
                  </select>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* ── Step 5: azione di stampa ─────────────────────────────────── */}
        <div className="bop-card">
          <div className="bop-card__body">
            <div className="bop-rep-dati-soc__actions">
              <button
                type="button"
                className="bop-btn bop-btn-primary"
                onClick={() => setPrintModalOpen(true)}
                disabled={!puoStampare}
                title="Apri il visualizzatore del report con i dati reali della società"
              >
                Stampa / Invia
              </button>
            </div>
            {!societaId && (
              <p className="bop-rep-dati-soc__hint">Seleziona una società per generare il report.</p>
            )}
            {societaId && !almenoUnaSezione && (
              <p className="bop-rep-dati-soc__hint">Seleziona almeno una sezione da includere.</p>
            )}
            {societaId && almenoUnaSezione && !haTemplate && (
              <p className="bop-rep-dati-soc__hint">Seleziona un template di stampa al passo 4.</p>
            )}
          </div>
        </div>

        {printModalOpen && societaId && societaSelezionata && (
          <PrintModal
            societaId={Number(societaId)}
            entitaType="dati_societari"
            entitaId={Number(societaId)}
            entitaLabel={`Dati completi — ${societaSelezionata.nome}`}
            extraOpts={extraOpts}
            onClose={() => setPrintModalOpen(false)}
          />
        )}

        <ApiInspector docs={API_DOCS} />
      </div>
    </Layout>
  );
}
